import api from '../../../api';

const BASE = '/api/workday-reference';

export interface WorkdayAlias {
  sourceFileScope: string;
  workdayProcess: string;
  condition?: string | null;
  sopTitle: string;
}

export interface WorkdayPacket {
  sourceFileScope?: string | null;
  workdayProcess?: string | null;
  condition?: string | null;
  excerpt?: string;
  editClass?: string;
}

export interface WorkdayNote {
  text?: string;
  reason?: string;
  sourceFileScope?: string | null;
  workdayProcess?: string | null;
  condition?: string | null;
  pointsAt?: string | null;
  referenceQuote?: string;
  sopQuote?: string | null;
  stepHeading?: string | null;
}

export interface WorkdayDisposition {
  id?: string;
  disposition?: string;
  referenceQuote?: string;
  sopQuote?: string;
  workdayProcess?: string | null;
  condition?: string | null;
  sourceFileScope?: string | null;
  sourceFilename?: string | null;
  reason?: string;
  holdKind?: string | null;
  moment?: string | null;
  text?: string;
  stepId?: string | null;
  stepHeading?: string | null;
  headingPath?: string | null;
  page?: number | null;
  recommendation?: 'keep_sop' | 'add_step' | 'leave' | string;
  rationale?: string;
  actions?: string[];
  resolution?: string | null;
  comment?: string | null;
  writes?: boolean;
  proposals?: WorkdayProposal[];
}

export interface WorkdayProposal {
  id?: string;
  disposition?: string;
  referenceQuote?: string;
  sopQuote?: string;
  proposed_content?: string;
  change_type?: string;
  stepHeading?: string | null;
  stepId?: string | null;
  workdayProcess?: string | null;
  condition?: string | null;
  sourceFileScope?: string | null;
  writes?: boolean;
  editClass?: string;
  rationale?: string | null;
  fromHold?: boolean;
}

export interface WorkdaySopChecklist {
  sopTitle?: string;
  sop?: Record<string, unknown>;
  dispositions?: WorkdayDisposition[];
  proposals?: WorkdayProposal[];
  holds?: WorkdayDisposition[];
  notes?: WorkdayNote[];
  alreadyThere?: WorkdayDisposition[];
  excluded?: WorkdayDisposition[];
  doNotWrite?: WorkdayDisposition[];
  sourceKind?: string;
  lastApply?: {
    wrote?: boolean;
    appliedIds?: string[];
    unmapped?: { id?: string; status?: string }[];
    docxBytes?: number;
  };
}

export interface WorkdayDeckPayload {
  filename?: string;
  fileScopes?: string[];
  packets?: WorkdayPacket[];
  notes?: WorkdayNote[];
  excluded?: { headingPath?: string; reason?: string; excerpt?: string }[];
  unresolved?: { headingPath?: string; excerpt?: string; reason?: string }[];
  aliases?: WorkdayAlias[];
  originalWordAliases?: WorkdayAlias[];
  sops?: WorkdaySopChecklist[];
  logicPackId?: string;
  logicPackVersion?: string;
  mode?: string;
  forcedProcesses?: Record<string, string>;
  lastSimplifiedResults?: WorkdayCompareResult[];
  lastSimplifiedCompare?: {
    at?: string;
    titles?: string[];
  };
}

export interface WorkdayReferenceSessionSummary {
  id: number;
  title?: string | null;
  status?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  logicPackId?: string | null;
  mode?: string | null;
  titles?: string[];
  comparedAt?: string | null;
  resultCount?: number;
}

export interface WorkdayReferenceRecord {
  id: number;
  title?: string | null;
  status?: string;
  payload?: WorkdayDeckPayload;
}

export interface WorkdayLogicPackProcess {
  key?: string;
  workdayProcess: string;
  summary?: string;
}

export interface WorkdayLogicPack {
  id: string;
  version: string;
  label: string;
  filename: string;
  fileScopes: string[];
  processCount: number;
  packetCount: number;
  processes?: WorkdayLogicPackProcess[];
}

export interface WorkdayProcessMatch {
  workdayProcess: string;
  sourceFileScope: string;
  score: number;
  summary?: string;
  key?: string | null;
}

export interface WorkdayPreviewMatch {
  sopTitle: string;
  ok: boolean;
  code: string | null;
  message: string | null;
  ambiguous: boolean;
  needsConfirmation: boolean;
  suggestedProcess: string | null;
  matches: WorkdayProcessMatch[];
  choices: WorkdayProcessMatch[];
}

export interface WorkdayCompareResult {
  sopTitle: string;
  kind: string;
  compared: boolean;
  blocked: boolean;
  code: string | null;
  message: string | null;
  agreesWithReference: false;
  status: string | null;
  suggestions: WorkdaySopChecklist | null;
  match: { aliases?: WorkdayAlias[]; matches?: WorkdayProcessMatch[] } | null;
  matches: WorkdayProcessMatch[];
  ambiguous: boolean;
}

export interface WorkdaySourceDoc {
  sopTitle: string;
  originalName: string;
  fileSize: number;
  sha256: string;
}

export interface WorkdaySourceComparison {
  compared: boolean;
  blocked: boolean;
  code: string | null;
  message: string | null;
  sopTitle: string;
  agreesWithReference: false;
  status: string | null;
  textAuthoritative: boolean;
  trackedChangesPreventedComparison: boolean;
  extractionPreventedComparison: boolean;
  suggestions: WorkdaySopChecklist | null;
}

function asRecord(value: unknown): WorkdayReferenceRecord {
  const row = (value || {}) as WorkdayReferenceRecord;
  return {
    ...row,
    id: Number(row.id),
    payload: row.payload || {},
  };
}

function asPack(value: unknown): WorkdayLogicPack | null {
  const row = (value || {}) as Partial<WorkdayLogicPack>;
  if (!row.id || !row.label) return null;
  return {
    id: String(row.id),
    version: String(row.version || ''),
    label: String(row.label),
    filename: String(row.filename || ''),
    fileScopes: Array.isArray(row.fileScopes) ? row.fileScopes.map(String) : [],
    processCount: Number(row.processCount) || 0,
    packetCount: Number(row.packetCount) || 0,
    processes: Array.isArray(row.processes)
      ? row.processes.map((item) => ({
        key: item?.key,
        workdayProcess: String(item?.workdayProcess || ''),
        summary: item?.summary,
      })).filter((item) => item.workdayProcess)
      : [],
  };
}

function asCompareResult(value: unknown): WorkdayCompareResult | null {
  const row = (value || {}) as Partial<WorkdayCompareResult>;
  if (typeof row.sopTitle !== 'string' || !row.sopTitle.trim()) return null;
  return {
    sopTitle: row.sopTitle,
    kind: typeof row.kind === 'string' ? row.kind : 'upload',
    compared: row.compared === true,
    blocked: row.blocked === true,
    code: typeof row.code === 'string' && row.code ? row.code : null,
    message: typeof row.message === 'string' && row.message.trim() ? row.message : null,
    agreesWithReference: false,
    status: typeof row.status === 'string' && row.status ? row.status : null,
    suggestions: row.suggestions && typeof row.suggestions === 'object' ? row.suggestions : null,
    match: row.match && typeof row.match === 'object' ? row.match : null,
    matches: Array.isArray(row.matches) ? row.matches : [],
    ambiguous: row.ambiguous === true,
  };
}

export async function listWorkdayLogicPacks(): Promise<WorkdayLogicPack[]> {
  const res = await api.get(`${BASE}/logic-packs`);
  if (!res.data?.success) throw new Error(res.data?.error || 'The Workday logic sets could not be loaded.');
  const rows = Array.isArray(res.data.packs) ? res.data.packs : [];
  return rows.map(asPack).filter((row: WorkdayLogicPack | null): row is WorkdayLogicPack => !!row);
}

export async function previewWorkdayMatches(input: {
  logicPackId: string;
  titles: string[];
}): Promise<WorkdayPreviewMatch[]> {
  const res = await api.post(`${BASE}/simplified/preview`, {
    logicPackId: input.logicPackId,
    titles: input.titles,
  });
  if (!res.data?.success) throw new Error(res.data?.error || 'Process matches could not be previewed.');
  const rows = Array.isArray(res.data.results) ? res.data.results : [];
  return rows.map((value: any) => ({
    sopTitle: String(value?.sopTitle || ''),
    ok: value?.ok === true,
    code: typeof value?.code === 'string' ? value.code : null,
    message: typeof value?.message === 'string' ? value.message : null,
    ambiguous: value?.ambiguous === true,
    needsConfirmation: value?.needsConfirmation !== false,
    suggestedProcess: typeof value?.suggestedProcess === 'string' ? value.suggestedProcess : null,
    matches: Array.isArray(value?.matches) ? value.matches : [],
    choices: Array.isArray(value?.choices) ? value.choices : [],
  })).filter((row: WorkdayPreviewMatch) => row.sopTitle);
}

export async function compareWorkdaySimplified(input: {
  logicPackId: string;
  sessionId?: number | null;
  selectedTitles: string[];
  sops: Record<string, unknown>[];
  uploads: { title: string; file: File }[];
  forcedProcesses?: Record<string, string>;
}): Promise<{ session: WorkdayReferenceRecord; pack: WorkdayLogicPack | null; results: WorkdayCompareResult[] }> {
  const form = new FormData();
  form.append('logicPackId', input.logicPackId);
  if (input.sessionId) form.append('sessionId', String(input.sessionId));
  form.append('selectedTitles', JSON.stringify(input.selectedTitles || []));
  form.append('sops', JSON.stringify(input.sops || []));
  form.append('forcedProcesses', JSON.stringify(input.forcedProcesses || {}));
  form.append('uploadTitles', JSON.stringify((input.uploads || []).map((row) => row.title)));
  for (const row of input.uploads || []) {
    form.append('files', row.file, row.file.name);
  }
  const res = await api.post(`${BASE}/simplified/compare`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000,
  });
  if (!res.data?.success) throw new Error(res.data?.error || 'The documents could not be compared.');
  const results = (Array.isArray(res.data.results) ? res.data.results : [])
    .map(asCompareResult)
    .filter((row: WorkdayCompareResult | null): row is WorkdayCompareResult => !!row);
  return {
    session: asRecord(res.data.session),
    pack: asPack(res.data.pack),
    results,
  };
}

export async function downloadWorkdayFlaggedDocx(
  sessionId: number,
  sopTitle: string,
  selectedChangeIds: string[],
): Promise<void> {
  try {
    const res = await api.post(
      `${BASE}/sessions/${sessionId}/flagged-docx`,
      { sopTitle, selected_change_ids: selectedChangeIds },
      { responseType: 'blob', timeout: 120000 },
    );
    const blob = new Blob([res.data]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const disposition = res.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="([^"]+)"/);
    a.download = match ? match[1] : `${sopTitle || 'sop'}-flagged.docx`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err: any) {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        if (parsed?.error) throw new Error(parsed.error);
      } catch (inner: any) {
        if (!(inner instanceof SyntaxError)) throw inner;
      }
    }
    throw err;
  }
}

export async function downloadWorkdayReferenceSourceDocx(id: number, sopTitle: string): Promise<void> {
  try {
    const res = await api.get(`${BASE}/sessions/${id}/source-docx`, {
      params: { sopTitle },
      responseType: 'blob',
      timeout: 60000,
    });
    const blob = new Blob([res.data]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const disposition = res.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="([^"]+)"/);
    a.download = match ? match[1] : `${sopTitle || 'sop'}.docx`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err: any) {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        if (parsed?.error) throw new Error(parsed.error);
      } catch (inner: any) {
        if (!(inner instanceof SyntaxError)) throw inner;
      }
    }
    throw err;
  }
}

export async function listWorkdayReferenceSourceDocs(id: number): Promise<WorkdaySourceDoc[]> {
  const res = await api.get(`${BASE}/sessions/${id}/source-docs`);
  if (!res.data?.success) throw new Error(res.data?.error || 'The source documents could not be listed.');
  const rows = Array.isArray(res.data.sourceDocs) ? res.data.sourceDocs : [];
  return rows.map((value: unknown) => {
    const row = (value || {}) as Partial<WorkdaySourceDoc>;
    const sopTitle = typeof row.sopTitle === 'string' ? row.sopTitle : '';
    const originalName = typeof row.originalName === 'string' ? row.originalName : '';
    const fileSize = Number(row.fileSize);
    const sha256 = typeof row.sha256 === 'string' ? row.sha256 : '';
    if (!sopTitle || !originalName || !Number.isFinite(fileSize)) return null;
    return { sopTitle, originalName, fileSize, sha256 };
  }).filter((row: WorkdaySourceDoc | null): row is WorkdaySourceDoc => !!row);
}

// Legacy helpers kept for older tests / backwards compatibility during rollout.
export async function createWorkdayReferenceSession(file: File, sops: Record<string, unknown>[]): Promise<WorkdayReferenceRecord> {
  const form = new FormData();
  form.append('file', file);
  form.append('filename', file.name);
  form.append('sops', JSON.stringify(sops));
  form.append('aliases', JSON.stringify([]));
  const res = await api.post(`${BASE}/sessions`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  if (!res.data?.success) throw new Error(res.data?.error || 'The deck was not read.');
  return asRecord(res.data.session);
}

export async function getWorkdayReferenceSession(id: number): Promise<WorkdayReferenceRecord> {
  const res = await api.get(`${BASE}/sessions/${id}`);
  if (!res.data?.success) throw new Error(res.data?.error || 'The checklist could not be loaded.');
  return asRecord(res.data.session);
}

export async function listWorkdayReferenceSessions(limit = 30): Promise<WorkdayReferenceSessionSummary[]> {
  const res = await api.get(`${BASE}/sessions`, { params: { limit } });
  if (!res.data?.success) throw new Error(res.data?.error || 'Past Workday compares could not be loaded.');
  const rows = Array.isArray(res.data.sessions) ? res.data.sessions : [];
  return rows.map((row: any) => ({
    id: Number(row?.id),
    title: typeof row?.title === 'string' ? row.title : null,
    status: typeof row?.status === 'string' ? row.status : null,
    createdAt: typeof row?.createdAt === 'string' ? row.createdAt : null,
    updatedAt: typeof row?.updatedAt === 'string' ? row.updatedAt : null,
    logicPackId: typeof row?.logicPackId === 'string' ? row.logicPackId : null,
    mode: typeof row?.mode === 'string' ? row.mode : null,
    titles: Array.isArray(row?.titles) ? row.titles.map(String) : [],
    comparedAt: typeof row?.comparedAt === 'string' ? row.comparedAt : null,
    resultCount: Number(row?.resultCount) || 0,
  })).filter((row: WorkdayReferenceSessionSummary) => Number.isFinite(row.id) && row.id > 0);
}

/** Rebuild compare cards from a persisted Workday reference session. */
export function compareResultsFromSession(session: WorkdayReferenceRecord | null | undefined): WorkdayCompareResult[] {
  const payload = session?.payload || {};
  const stored = Array.isArray(payload.lastSimplifiedResults) ? payload.lastSimplifiedResults : [];
  if (stored.length) {
    return stored.map(asCompareResult).filter((row): row is WorkdayCompareResult => !!row);
  }
  return (payload.sops || []).map((row) => {
    const sopTitle = String(row?.sopTitle || '').trim();
    if (!sopTitle) return null;
    return {
      sopTitle,
      kind: typeof row?.sourceKind === 'string' ? row.sourceKind : 'upload',
      compared: true,
      blocked: false,
      code: null,
      message: null,
      agreesWithReference: false as const,
      status: 'suggestions_for_human_review',
      suggestions: row,
      match: null,
      matches: [],
      ambiguous: false,
    };
  }).filter((row): row is WorkdayCompareResult => !!row);
}
